import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import StarOutlineIcon from '@mui/icons-material/StarOutline';

import { useToast } from '../../../../common/hooks/useToast';
import { addressLines, ContactDetail, formatDay } from '../contactDetail';
import {
  AddressForm,
  addressFormOf,
  addressInput,
  addressReady,
  callableItem,
  EmailItem,
  emailItems,
  freePhoneTypes,
  looksLikeEmail,
  nextGoodNumber,
  numberHint,
  PHONE_TYPES,
  PhoneItem,
  phoneItems,
  PhoneType,
  phoneTypeWord,
  placeFromAddress,
} from './contactEdit';
import {
  CRM_ADD_CONTACT_ADDRESS,
  CRM_ADD_CONTACT_EMAIL,
  CRM_ADD_CONTACT_PHONE,
  CRM_REMOVE_CONTACT_ADDRESS,
  CRM_REMOVE_CONTACT_EMAIL,
  CRM_REMOVE_CONTACT_PHONE,
  CRM_UPDATE_CONTACT_ADDRESS,
  CRM_UPDATE_CONTACT_EMAIL,
  CRM_UPDATE_CONTACT_PHONE,
} from './contactEdit.queries';
import { CountryField } from './CountryField';
import { useContactEdit } from './useContactEdit';

/**
 * A person's emails, numbers and addresses (card 21.5), each with a menu:
 * a hook that hands the page each list to place, and the dialogs they open.
 * make it the main one, mark it bad or good, correct it, remove it. Plus "Add".
 *
 * - **Marking is one click**, and a toast says what it did: where calls go now,
 *   or that the person cannot be called until a good number is added.
 * - **Correcting and adding** open a small dialog; removing asks first.
 * - **Every change is logged** by the API and shows on the timeline, which
 *   keeps the old value.
 * - A merged-away record shows its details with no controls.
 */

type Address = ContactDetail['addresses'][number];

const Flag = ({ label, tone = 'default' }: { label: string; tone?: 'default' | 'error' | 'warning' | 'primary' }) => (
  <Chip size="small" label={label} color={tone} variant="outlined" sx={{ height: 20, fontSize: '0.6875rem', ml: 0.75 }} />
);

/** One row: the value, its flags, and its menu button. */
const Row = ({ children, menu }: { children: React.ReactNode; menu?: React.ReactNode }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, minHeight: 28, '&:hover .crm-row-menu': { opacity: 1 } }}>
    <Box sx={{ flex: 1, minWidth: 0, py: 0.25 }}>{children}</Box>
    {menu}
  </Box>
);

interface Action {
  label: string;
  icon: React.ReactElement;
  onClick: () => void;
  danger?: boolean;
}

const RowMenu = ({ label, actions }: { label: string; actions: Array<Action> }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  if (!actions.length) return null;
  return (
    <>
      <IconButton
        size="small"
        className="crm-row-menu"
        aria-label={label}
        aria-haspopup="menu"
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{ p: 0.25, mt: -0.125, opacity: { xs: 1, md: 0.55 }, '&:focus-visible': { opacity: 1 } }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        {actions.map((a) => (
          <MenuItem
            key={a.label}
            onClick={() => {
              setAnchor(null);
              a.onClick();
            }}
            sx={a.danger ? { color: 'error.main' } : undefined}
          >
            <ListItemIcon sx={a.danger ? { color: 'error.main' } : undefined}>{a.icon}</ListItemIcon>
            <ListItemText>{a.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const AddButton = ({ label, onClick, disabled, title }: { label: string; onClick: () => void; disabled?: boolean; title?: string }) => (
  <Tooltip title={title ?? ''} describeChild>
    <span>
      <Button size="small" startIcon={<AddIcon />} onClick={onClick} disabled={disabled} sx={{ mt: 0.25, ml: -0.75, py: 0, minHeight: 0 }}>
        {label}
      </Button>
    </span>
  </Tooltip>
);

/* ------------------------------------------------------------------ *
 * Dialogs
 * ------------------------------------------------------------------ */

const EditDialog = ({
  title,
  children,
  error,
  loading,
  ready,
  submitLabel,
  danger,
  onClose,
  onSubmit,
}: {
  title: string;
  children: React.ReactNode;
  error: { message: string } | null;
  loading: boolean;
  ready: boolean;
  submitLabel: string;
  danger?: boolean;
  onClose: () => void;
  onSubmit: () => void;
}) => (
  <Dialog open onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth aria-labelledby="crm-point-title">
    <Box
      component="form"
      noValidate
      onSubmit={(event: React.FormEvent) => {
        event.preventDefault();
        if (ready && !loading) onSubmit();
      }}
    >
      <DialogTitle id="crm-point-title">{title}</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '8px !important' }}>
        {children}
        {error && <Alert severity="error">{error.message}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          color={danger ? 'error' : 'primary'}
          disabled={!ready || loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {submitLabel}
        </Button>
      </DialogActions>
    </Box>
  </Dialog>
);

const PhoneDialog = ({
  contact,
  phone,
  onClose,
  onDone,
}: {
  contact: ContactDetail;
  /** The number to correct; none to add one. */
  phone?: PhoneItem;
  onClose: () => void;
  onDone: () => void;
}) => {
  const free = freePhoneTypes(contact);
  const [type, setType] = useState<PhoneType>(free[0] ?? 'MOBILE');
  const [number, setNumber] = useState(phone?.text ?? '');
  const [makePrimary, setMakePrimary] = useState(!contact.primaryPhone);
  const add = useContactEdit(CRM_ADD_CONTACT_PHONE, onDone);
  const update = useContactEdit(CRM_UPDATE_CONTACT_PHONE, onDone);
  const { loading, error } = phone ? update : add;
  const hint = numberHint(number, contact.countryCode);
  const ready = Boolean(number.trim()) && !hint?.error && (!phone || number.trim() !== phone.text);

  const submit = () =>
    phone
      ? update.run({ contactId: contact.id, phoneId: phone.id, change: { number: number.trim(), ...(makePrimary && !phone.isPrimary ? { makePrimary: true } : {}) } })
      : add.run({ contactId: contact.id, type, number: number.trim(), makePrimary: makePrimary && type !== 'FAX' });

  return (
    <EditDialog
      title={phone ? `Correct the ${phoneTypeWord(phone.type).toLowerCase()} number` : 'Add a number'}
      error={error}
      loading={loading}
      ready={ready}
      submitLabel={phone ? 'Save' : 'Add'}
      onClose={onClose}
      onSubmit={submit}
    >
      {!phone && (
        <TextField select label="Which" value={type} onChange={(e) => setType(e.target.value as PhoneType)} size="small">
          {PHONE_TYPES.filter((t) => free.includes(t.value)).map((t) => (
            <MenuItem key={t.value} value={t.value}>
              {t.label}
            </MenuItem>
          ))}
        </TextField>
      )}
      <TextField
        label="Number"
        value={number}
        onChange={(e) => setNumber(e.target.value)}
        size="small"
        autoFocus
        inputMode="tel"
        placeholder={contact.countryCode === 'GH' ? '024 123 4567' : '+233 24 123 4567'}
        error={Boolean(hint?.error) || error?.field === 'number'}
        helperText={hint?.text ?? 'With + and the country code, or as dialled in their country.'}
        inputProps={{ maxLength: 40, 'aria-label': 'Number' }}
      />
      {phone && <DialogContentText variant="body2">A corrected number is no longer marked bad. The old one stays on their timeline.</DialogContentText>}
      {(!phone || !phone.isPrimary) && (phone ? phone.type !== 'FAX' : type !== 'FAX') && (
        <FormControlLabel
          control={<Checkbox checked={makePrimary} onChange={(e) => setMakePrimary(e.target.checked)} size="small" />}
          label="Calls go to this number"
        />
      )}
    </EditDialog>
  );
};

const EmailDialog = ({ contact, email, onClose, onDone }: { contact: ContactDetail; email?: EmailItem; onClose: () => void; onDone: () => void }) => {
  const [value, setValue] = useState(email?.email ?? '');
  const [makePrimary, setMakePrimary] = useState(!contact.primaryEmail);
  const add = useContactEdit(CRM_ADD_CONTACT_EMAIL, onDone);
  const update = useContactEdit(CRM_UPDATE_CONTACT_EMAIL, onDone);
  const { loading, error } = email ? update : add;
  const typed = value.trim().toLowerCase();
  const valid = looksLikeEmail(typed);
  const ready = valid && (!email || typed !== email.email);

  const submit = () =>
    email
      ? update.run({ contactId: contact.id, emailId: email.id, change: { email: typed, ...(makePrimary && !email.isPrimary ? { makePrimary: true } : {}) } })
      : add.run({ contactId: contact.id, email: typed, makePrimary });

  return (
    <EditDialog
      title={email ? 'Correct the address' : 'Add an email address'}
      error={error}
      loading={loading}
      ready={ready}
      submitLabel={email ? 'Save' : 'Add'}
      onClose={onClose}
      onSubmit={submit}
    >
      <TextField
        label="Email"
        type="email"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        size="small"
        autoFocus
        error={(value.trim() !== '' && !valid) || Boolean(error?.field)}
        helperText={value.trim() !== '' && !valid ? 'A name, an @ and a domain: ama@example.com.' : email ? 'A corrected address is no longer marked bad.' : ' '}
        inputProps={{ maxLength: 254 }}
      />
      {(!email || !email.isPrimary) && (
        <FormControlLabel
          control={<Checkbox checked={makePrimary} onChange={(e) => setMakePrimary(e.target.checked)} size="small" />}
          label="Emails go to this address"
        />
      )}
    </EditDialog>
  );
};

const AddressDialog = ({ contact, address, onClose, onDone }: { contact: ContactDetail; address?: Address; onClose: () => void; onDone: () => void }) => {
  const [form, setForm] = useState<AddressForm>(() => addressFormOf(address, contact));
  const willBeMain = !address ? !contact.addresses.some((a) => a.isPrimary) : address.isPrimary;
  const [makePrimary, setMakePrimary] = useState(willBeMain);
  const [usePlace, setUsePlace] = useState(willBeMain);
  const add = useContactEdit(CRM_ADD_CONTACT_ADDRESS, onDone);
  const update = useContactEdit(CRM_UPDATE_CONTACT_ADDRESS, onDone);
  const { loading, error } = address ? update : add;
  const set = (key: keyof AddressForm) => (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const place = placeFromAddress(form);

  const submit = () =>
    address
      ? update.run({
          contactId: contact.id,
          addressId: address.id,
          change: { address: addressInput(form), ...(makePrimary && !address.isPrimary ? { makePrimary: true } : {}), ...(usePlace ? { usePlace: true } : {}) },
        })
      : add.run({ contactId: contact.id, address: addressInput(form), makePrimary, usePlace });

  return (
    <EditDialog
      title={address ? 'Correct the address' : 'Add an address'}
      error={error}
      loading={loading}
      ready={addressReady(form)}
      submitLabel={address ? 'Save' : 'Add'}
      onClose={onClose}
      onSubmit={submit}
    >
      <TextField label="Address" value={form.address1} onChange={set('address1')} size="small" autoFocus inputProps={{ maxLength: 100 }} />
      <TextField label="Address, second line" value={form.address2} onChange={set('address2')} size="small" inputProps={{ maxLength: 100 }} />
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: '1fr 1fr' }}>
        <TextField label="Town or city" value={form.city} onChange={set('city')} size="small" inputProps={{ maxLength: 100 }} />
        <TextField label="Region or state" value={form.state} onChange={set('state')} size="small" inputProps={{ maxLength: 100 }} />
        <TextField label="Postcode" value={form.zip} onChange={set('zip')} size="small" inputProps={{ maxLength: 20 }} />
        <CountryField value={form.countryCode} onChange={(code) => setForm((f) => ({ ...f, countryCode: code }))} />
      </Box>
      {!(address?.isPrimary ?? false) && (
        <FormControlLabel
          control={<Checkbox checked={makePrimary} onChange={(e) => setMakePrimary(e.target.checked)} size="small" />}
          label="Their main address"
        />
      )}
      <FormControlLabel
        control={<Checkbox checked={usePlace} onChange={(e) => setUsePlace(e.target.checked)} size="small" />}
        label={place ? `Also set their place to ${place}` : 'Also set their place from it'}
      />
    </EditDialog>
  );
};

const RemoveDialog = ({
  what,
  consequence,
  mutation,
  variables,
  onClose,
  onDone,
}: {
  what: string;
  consequence?: string;
  mutation: Parameters<typeof useContactEdit>[0];
  variables: Record<string, unknown>;
  onClose: () => void;
  onDone: () => void;
}) => {
  const { run, loading, error } = useContactEdit(mutation, onDone);
  return (
    <EditDialog title={`Remove ${what}?`} error={error} loading={loading} ready submitLabel="Remove" danger onClose={onClose} onSubmit={() => run(variables)}>
      <DialogContentText variant="body2">
        {consequence ? `${consequence} ` : ''}It stays on their timeline, so it can be added back.
      </DialogContentText>
    </EditDialog>
  );
};

/* ------------------------------------------------------------------ *
 * The rows
 * ------------------------------------------------------------------ */

type Open =
  | { kind: 'phone'; phone?: PhoneItem }
  | { kind: 'email'; email?: EmailItem }
  | { kind: 'address'; address?: Address }
  | { kind: 'removePhone'; phone: PhoneItem }
  | { kind: 'removeEmail'; email: EmailItem }
  | { kind: 'removeAddress'; address: Address };

interface PointsProps {
  contact: ContactDetail;
  /** Shown struck through, with the flag: do not call or do not contact. */
  noCalls: boolean;
  onChanged: () => void;
}

/** One-click edits: mark bad or good, make main. Says what happened in a toast. */
const useQuickEdits = (contact: ContactDetail, onChanged: () => void) => {
  const toast = useToast();
  const phone = useContactEdit(CRM_UPDATE_CONTACT_PHONE, onChanged);
  const email = useContactEdit(CRM_UPDATE_CONTACT_EMAIL, onChanged);
  const say = (result: { ok: boolean; error?: { message: string } }, message: string) =>
    result.ok ? toast.success(message) : toast.error(result.error?.message ?? 'Not saved. Try again.');

  const phoneChange = async (p: PhoneItem, change: Record<string, boolean>, message: string) =>
    say(await phone.run({ contactId: contact.id, phoneId: p.id, change }), message);
  const emailChange = async (e: EmailItem, change: Record<string, boolean>, message: string) =>
    say(await email.run({ contactId: contact.id, emailId: e.id, change }), message);

  return {
    busy: phone.loading || email.loading,
    markPhone: (p: PhoneItem, bad: boolean) => {
      if (!bad) return phoneChange(p, { isBad: false }, `${p.text} marked good.`);
      if (!p.isPrimary) return phoneChange(p, { isBad: true }, `${p.text} marked bad.`);
      const next = nextGoodNumber(contact, p);
      return phoneChange(
        p,
        { isBad: true },
        next
          ? `${p.text} marked bad. Calls now go to ${next.text}${next.type ? ` (${phoneTypeWord(next.type).toLowerCase()})` : ''}.`
          : `${p.text} marked bad. They cannot be called or texted until a good number is added.`,
      );
    },
    mainPhone: (p: PhoneItem) => phoneChange(p, { makePrimary: true }, `Calls now go to ${p.text}.`),
    markEmail: (e: EmailItem, bad: boolean) =>
      emailChange(e, { isBad: bad }, bad ? `${e.email} marked bad: it is not emailed.` : `${e.email} marked good.`),
    mainEmail: (e: EmailItem) => emailChange(e, { makePrimary: true }, `Emails now go to ${e.email}.`),
  };
};

export const useContactPoints = ({ contact, noCalls, onChanged }: PointsProps) => {
  const editable = contact.mergedIntoId == null;
  const [open, setOpen] = useState<Open | null>(null);
  const quick = useQuickEdits(contact, onChanged);
  const done = () => {
    setOpen(null);
    onChanged();
  };
  const phones = phoneItems(contact);
  const emails = emailItems(contact);
  const freeTypes = freePhoneTypes(contact);

  const phoneActions = (p: PhoneItem): Array<Action> => {
    if (!editable) return [];
    const word = phoneTypeWord(p.type).toLowerCase();
    return [
      ...(!p.isPrimary && !p.isBad && callableItem(p)
        ? [{ label: 'Calls go to this number', icon: <StarOutlineIcon fontSize="small" />, onClick: () => quick.mainPhone(p) }]
        : []),
      p.isBad
        ? { label: 'Mark good', icon: <CheckCircleOutlineIcon fontSize="small" />, onClick: () => quick.markPhone(p, false) }
        : { label: 'Mark bad (wrong number)', icon: <ReportGmailerrorredIcon fontSize="small" />, onClick: () => quick.markPhone(p, true) },
      { label: 'Correct the number…', icon: <EditOutlinedIcon fontSize="small" />, onClick: () => setOpen({ kind: 'phone', phone: p }) },
      ...(p.id != null
        ? [{ label: `Remove the ${word} number…`, icon: <DeleteOutlineIcon fontSize="small" />, danger: true, onClick: () => setOpen({ kind: 'removePhone', phone: p }) }]
        : []),
    ];
  };

  const emailActions = (e: EmailItem): Array<Action> => {
    if (!editable) return [];
    return [
      ...(!e.isPrimary && !e.bad ? [{ label: 'Emails go to this address', icon: <StarOutlineIcon fontSize="small" />, onClick: () => quick.mainEmail(e) }] : []),
      e.bad
        ? { label: 'Mark good', icon: <CheckCircleOutlineIcon fontSize="small" />, onClick: () => quick.markEmail(e, false) }
        : { label: 'Mark bad', icon: <ReportGmailerrorredIcon fontSize="small" />, onClick: () => quick.markEmail(e, true) },
      { label: 'Correct the address…', icon: <EditOutlinedIcon fontSize="small" />, onClick: () => setOpen({ kind: 'email', email: e }) },
      ...(e.id != null
        ? [{ label: 'Remove…', icon: <DeleteOutlineIcon fontSize="small" />, danger: true, onClick: () => setOpen({ kind: 'removeEmail', email: e }) }]
        : []),
    ];
  };

  const addressActions = (a: Address): Array<Action> => {
    if (!editable) return [];
    return [
      { label: 'Correct the address…', icon: <EditOutlinedIcon fontSize="small" />, onClick: () => setOpen({ kind: 'address', address: a }) },
      { label: 'Remove…', icon: <DeleteOutlineIcon fontSize="small" />, danger: true, onClick: () => setOpen({ kind: 'removeAddress', address: a }) },
    ];
  };

  const empty = (
    <Box component="span" sx={{ color: '#cbd5e1' }}>
      —
    </Box>
  );

  return {
    emails: (
      <Box>
        {emails.length === 0 && empty}
        {emails.map((e) => (
          <Row key={e.id ?? 'app'} menu={<RowMenu label={`Actions for ${e.email}`} actions={emailActions(e)} />}>
            <Box component="span" sx={e.bad ? { textDecoration: 'line-through', color: 'text.secondary' } : undefined}>
              {e.email}
            </Box>
            {e.isPrimary && emails.length > 1 && <Flag label="Main" tone="primary" />}
            {e.bad && <Flag label={e.bouncedAt ? `Bounced ${formatDay(e.bouncedAt)}` : 'Bad'} tone="error" />}
            {e.fromApp && <Flag label="From the app" />}
          </Row>
        ))}
        {editable && (
          <AddButton
            label="Add an email"
            onClick={() => setOpen({ kind: 'email' })}
            disabled={contact.emails.length >= 4}
            title={contact.emails.length >= 4 ? 'Four addresses is the most kept. Remove one first.' : ''}
          />
        )}
      </Box>
    ),
    phones: (
      <Box>
        {phones.length === 0 && empty}
        {phones.map((p) => (
          <Row key={p.id ?? 'app'} menu={<RowMenu label={`Actions for ${p.text}`} actions={phoneActions(p)} />}>
            <Box component="span" sx={noCalls || p.isBad ? { textDecoration: 'line-through', color: 'text.secondary' } : undefined}>
              {p.e164 && !noCalls && !p.isBad ? (
                <Link href={`tel:${p.e164}`} underline="hover" color="inherit">
                  {p.text}
                </Link>
              ) : (
                p.text
              )}
            </Box>
            {p.type && (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                {p.type.toLowerCase()}
              </Typography>
            )}
            {noCalls && <Flag label={contact.doNotContact ? 'Do not contact' : 'Do not call'} tone="error" />}
            {p.isPrimary && phones.length > 1 && <Flag label="Main" tone="primary" />}
            {p.isBad && <Flag label="Bad number" tone="error" />}
            {p.fromApp && <Flag label="From the app" />}
            {!p.e164 && (
              <Tooltip title="NationBuilder's number could not be read as an international number, so it cannot be dialled as stored. Correct it to dial it.">
                <span>
                  <Flag label="Not diallable" tone="warning" />
                </span>
              </Tooltip>
            )}
          </Row>
        ))}
        {editable && (
          <AddButton
            label="Add a number"
            onClick={() => setOpen({ kind: 'phone' })}
            disabled={freeTypes.length === 0}
            title={freeTypes.length === 0 ? 'They have a mobile, home, work and fax number. Correct one instead.' : ''}
          />
        )}
      </Box>
    ),
    addresses: (
      <Box>
        {contact.addresses.length === 0 && empty}
        {contact.addresses.map((a) => (
          <Row key={a.id} menu={<RowMenu label={`Actions for ${addressLines(a).join(', ')}`} actions={addressActions(a)} />}>
            {addressLines(a).join(', ')}
            {a.isPrimary && contact.addresses.length > 1 && <Flag label="Main" tone="primary" />}
            {a.roles
              .filter((role) => role !== 'PRIMARY')
              .map((role) => (
                <Flag key={role} label={role.toLowerCase().replace(/_/g, ' ')} />
              ))}
          </Row>
        ))}
        {editable && <AddButton label="Add an address" onClick={() => setOpen({ kind: 'address' })} />}
      </Box>
    ),
    dialogs: (
      <>
        {open?.kind === 'phone' && <PhoneDialog contact={contact} phone={open.phone} onClose={() => setOpen(null)} onDone={done} />}
        {open?.kind === 'email' && <EmailDialog contact={contact} email={open.email} onClose={() => setOpen(null)} onDone={done} />}
        {open?.kind === 'address' && <AddressDialog contact={contact} address={open.address} onClose={() => setOpen(null)} onDone={done} />}
        {open?.kind === 'removePhone' && (
          <RemoveDialog
            what={open.phone.text}
            consequence={open.phone.isPrimary ? 'Calls will go to their next good number, if they have one.' : undefined}
            mutation={CRM_REMOVE_CONTACT_PHONE}
            variables={{ contactId: contact.id, phoneId: open.phone.id }}
            onClose={() => setOpen(null)}
            onDone={done}
          />
        )}
        {open?.kind === 'removeEmail' && (
          <RemoveDialog
            what={open.email.email}
            consequence={open.email.isPrimary ? 'Emails will go to their next good address, if they have one.' : undefined}
            mutation={CRM_REMOVE_CONTACT_EMAIL}
            variables={{ contactId: contact.id, emailId: open.email.id }}
            onClose={() => setOpen(null)}
            onDone={done}
          />
        )}
        {open?.kind === 'removeAddress' && (
          <RemoveDialog
            what="this address"
            consequence={open.address.isPrimary && contact.addresses.length > 1 ? 'Their next address becomes the main one.' : undefined}
            mutation={CRM_REMOVE_CONTACT_ADDRESS}
            variables={{ contactId: contact.id, addressId: open.address.id }}
            onClose={() => setOpen(null)}
            onDone={done}
          />
        )}
      </>
    ),
  };
};
